import { useState, useEffect, useCallback } from "react";
import { getPacienteAuthMe } from "../api/pacientesAuthMe.api";
import axios from "axios";

interface PatientDataState {
  hasPatientData: boolean;
  isLoading: boolean;
  error: string | null;
  statusCode: number | null;
}

/**
 * Hook personalizado para verificar si el paciente tiene datos registrados.
 * Intercepta errores 404 del endpoint /Auth/me/paciente y gestiona el estado de la validación.
 * 
 * @returns {PatientDataState} Estado de los datos del paciente
 */
export const usePatientDataValidation = () => {
  const [state, setState] = useState<PatientDataState>({
    hasPatientData: true, // Asume true inicialmente para no bloquear prematuramente
    isLoading: true,
    error: null,
    statusCode: null,
  });

  const checkPatientData = useCallback(async () => {
    try {
      setState((prev) => ({ ...prev, isLoading: true, error: null }));
      
      // Intenta obtener los datos del paciente autenticado
      await getPacienteAuthMe();
      
      // Si la llamada es exitosa, el paciente tiene datos
      setState({
        hasPatientData: true,
        isLoading: false,
        error: null,
        statusCode: 200,
      });
    } catch (error: unknown) {
      // Manejo específico de errores HTTP
      if (axios.isAxiosError(error)) {
        const statusCode = error.response?.status || null;
        const errorMessage =
          error.response?.data?.message ||
          error.message ||
          "Error al verificar datos del paciente";

        // Si es un 404 o 400, significa que no tiene datos registrados
        if (statusCode === 404 || statusCode === 400) {
          console.log('🚫 Paciente sin datos registrados - Bloqueando aplicación');
          setState({
            hasPatientData: false,
            isLoading: false,
            error: errorMessage,
            statusCode: statusCode,
          });
        } else {
          // Para otros errores, asumir que hay un problema de conexión
          // pero no bloquear la aplicación (puede ser temporal)
          console.warn(`⚠️ Error ${statusCode} al verificar paciente - Permitiendo acceso temporal`);
          setState({
            hasPatientData: true,
            isLoading: false,
            error: errorMessage,
            statusCode: statusCode,
          });
        }
      } else if (error instanceof Error) {
        // Error de JavaScript o de otro tipo
        const errorMessage = error.message || "Error desconocido al verificar datos del paciente";
        
        // Si el mensaje indica que no hay datos del paciente, bloquear
        if (errorMessage.toLowerCase().includes("no encontrado") || 
            errorMessage.toLowerCase().includes("no tiene la estructura esperada")) {
          console.log('🚫 Paciente sin datos - Bloqueando aplicación');
          setState({
            hasPatientData: false,
            isLoading: false,
            error: errorMessage,
            statusCode: 404,
          });
        } else {
          console.warn('⚠️ Error desconocido - Permitiendo acceso temporal');
          setState({
            hasPatientData: true,
            isLoading: false,
            error: errorMessage,
            statusCode: null,
          });
        }
      } else {
        // Error desconocido
        console.warn('⚠️ Error desconocido - Permitiendo acceso temporal');
        setState({
          hasPatientData: true,
          isLoading: false,
          error: "Error desconocido al verificar datos del paciente",
          statusCode: null,
        });
      }
    }
  }, []);

  // Verificar datos del paciente al montar el componente
  useEffect(() => {
    checkPatientData();
  }, [checkPatientData]);

  return {
    ...state,
    refetch: checkPatientData, // Permite re-verificar manualmente
  };
};
